import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Image,
  Container,
  Card,
  Button,
  Nav,
  ListGroup,
  Form,
  Spinner,
  Modal,
} from "react-bootstrap";
import { useParams } from "react-router-dom";
import { IntakeForm } from "./intakeForm";
import { getPracticeByNumberPublic } from "../apracticeidapi";
import { getOnlineBooking } from "../acommsapi";
import {
  getServicesByPracticeNumbersPublic,
  getPractitionersByPracticeNumbersPublic,
  getAvailableAppointmentSlots,
} from "../api";
import { getOzowOnlinePaymentStatus } from "../afinanceapi";
import { BookingSummary } from "../Components/Practice/GoogleMaps";
import { createOnlineBookingSession, cancelOnlineBookingSession } from "../intakeApi";
import "./home.css";

function MonthCalendar({ selectedDate, onSelectDate }) {
  const today = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [currentMonth, setCurrentMonth] = useState(selectedDate.getMonth());
  const [currentYear, setCurrentYear] = useState(selectedDate.getFullYear());

  // When selectedDate changes from parent, sync month/year
  useEffect(() => {
    setCurrentMonth(selectedDate.getMonth());
    setCurrentYear(selectedDate.getFullYear());
  }, [selectedDate]);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getDaysInMonth = (month, year) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentYear, currentMonth, day);
    clickedDate.setHours(12, 0, 0, 0);

    const clickedMidnight = new Date(currentYear, currentMonth, day);
    clickedMidnight.setHours(0, 0, 0, 0);

    // ⛔ Ignore clicks on past dates
    if (clickedMidnight < today) return;

    onSelectDate(clickedDate);
  };

  const renderCalendar = () => {
    const days = [];
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`e-${i}`} className="p-2"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(currentYear, currentMonth, day);
      dayDate.setHours(0, 0, 0, 0);
      const isPast = dayDate < today;

      const isSelected =
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === currentMonth &&
        selectedDate.getFullYear() === currentYear;

      days.push(
        <div
          key={day}
          className={`p-2 text-center border ${
            isSelected ? "bg-primary text-white" : ""
          } ${isPast ? "text-muted bg-light" : "hover:bg-light"}`}
          style={{
            cursor: isPast ? "not-allowed" : "pointer",
            opacity: isPast ? 0.5 : 1,
          }}
          onClick={() => {
            if (!isPast) handleDateClick(day);
          }}
        >
          {day}
        </div>
      );
    }
    return days;
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => {
            setCurrentMonth((prev) => {
              if (prev === 0) {
                setCurrentYear((y) => y - 1);
                return 11;
              }
              return prev - 1;
            });
          }}
        >
          ‹
        </Button>
        <h6 className="mb-0">
          {monthNames[currentMonth]} {currentYear}
        </h6>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => {
            setCurrentMonth((prev) => {
              if (prev === 11) {
                setCurrentYear((y) => y + 1);
                return 0;
              }
              return prev + 1;
            });
          }}
        >
          ›
        </Button>
      </Card.Header>
      <Card.Body>
        <div className="row g-0 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="col text-center fw-bold small p-1">
              {day}
            </div>
          ))}
        </div>
        <div
          className="d-grid"
          style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: "1px" }}
        >
          {renderCalendar()}
        </div>
      </Card.Body>
    </Card>
  );
}

export function OnlineBooking() {
  const { id } = useParams();
  const [onlineBooking, setOnlineBooking] = useState(null);
  const [services, setServices] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [practices, setPractices] = useState([]);
  const [flow, setFlow] = useState(null); // 'service-first' | 'prac-first'
  const [step, setStep] = useState(0);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [times, setTimes] = useState({});
  const [creatingSession, setCreatingSession] = useState(false);
  const [intakeSession, setIntakeSession] = useState(null);
  const [holdSecondsLeft, setHoldSecondsLeft] = useState(null);
  const [holdExpired, setHoldExpired] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(null);
  const [ozowStatus, setOzowStatus] = useState(null);
  const [transactionRef, setTransactionRef] = useState("");
  const [isUpfrontPayable, setIsUpfrontPayable] = useState(false);
  const [isOnlinePaymentEnabled, setIsOnlinePaymentEnabled] = useState(false);
  const [amount, setAmount] = useState(0);

  useEffect(() => {
    async function fetchData() {
      const ob = await getOnlineBooking(id);
      setOnlineBooking(ob);
      console.log("Online Booking Data:", ob);
      setPractitioners(
        await getPractitionersByPracticeNumbersPublic(ob.selectedPractices)
      );
      console.log("Practitioners Data:", practitioners);

      setPractices(await getPracticeByNumberPublic(ob.selectedPractices));
      console.log("pracData:", practices);
      setServices(
        await getServicesByPracticeNumbersPublic(ob.selectedPractices)
      );
      console.log("Services Data:", services);
      setOzowStatus(await getOzowOnlinePaymentStatus(ob.selectedPractices));
      console.log("Ozow Status:", ozowStatus);
      setPractitioners(
        await getPractitionersByPracticeNumbersPublic(ob.selectedPractices)
      );
      console.log("Practitioners Data:", practitioners);
    }
    fetchData();
  }, [id]);

  // Auto-select if only ONE practitioner
  useEffect(() => {
    if (practitioners.length === 1 && !selectedPractitioner) {
      setSelectedPractitioner(practitioners[0]);
    }
  }, [practitioners, selectedPractitioner]);

  // Auto-select if only ONE practice
  useEffect(() => {
    if (practices.length === 1 && !selectedPractice) {
      const onlyPractice = practices[0];
      setSelectedPractice(onlyPractice);

      // If it only has one location, select it by default as well
      const locs = onlyPractice.locations || [];
      if (locs.length === 1) {
        setSelectedLocation(locs[0]);
      }
    }
  }, [practices, selectedPractice]);

  const generateTransactionRef = (practitionerId) => {
    const randomPart = Math.floor(1000000000 + Math.random() * 9000000000); // 10-digit random number
    return `OP${randomPart}`; // Always starts with OP and is 12 digits + OP = 14
  };

useEffect(() => {
  async function getASlots() {
    if (
      !onlineBooking ||
      !selectedPractitioner ||
      !selectedService ||
      !selectedPractice ||
      !selectedLocation ||
      !selectedDate
    ) {
      return;
    }

    const formattedDate = selectedDate.toISOString().split("T")[0]; // e.g. "2025-06-11"

    const { slots = [] } = await getAvailableAppointmentSlots({
      date: formattedDate,
      serviceDuration: selectedService.servicesDuration,
      overlapPadding: onlineBooking.allowOverPadding,
      overlapMinutes: onlineBooking.overlapMinutes,
      leadingMinutes: onlineBooking.leadingMinutes, // send raw to API as you did
      resourceId: selectedPractitioner.id,
      selectedPractice: selectedPractice.practiceNumber,
      selectedLocationName: selectedLocation?.name || "",
    });

    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];

    let filteredSlots = slots;

    if (formattedDate === todayStr) {
      // Always exclude past times + respect lead time
      const now = new Date();

      // 🔹 Parse leadingMinutes which can be "HH:MM" or a number-like value
      const lm = onlineBooking.leadingMinutes;
      let leadMinutes = 0;

      if (typeof lm === "string" && lm.includes(":")) {
        const [hh, mm] = lm.split(":").map((v) => parseInt(v, 10) || 0);
        leadMinutes = hh * 60 + mm;
      } else {
        leadMinutes = Number(lm) || 0;
      }

      const cutoff = new Date(now.getTime() + leadMinutes * 60000);

      filteredSlots = slots.filter((slot) => {
        if (!slot?.start) return false;
        const slotStart = new Date(slot.start);
        // ❌ hide any slot that starts before now + leadTime
        return slotStart >= cutoff;
      });
    }

    setAvailableSlots(filteredSlots);
    setSelectedTime(null);

    const matchedPracticeStatus = ozowStatus.find(
      (entry) => entry.practiceNumber === selectedPractice.practiceNumber
    );
    setIsOnlinePaymentEnabled(matchedPracticeStatus?.ozow?.onlinePayment);
    setTransactionRef(generateTransactionRef(selectedPractitioner.id));
    setIsUpfrontPayable(selectedService.settings?.upfrontPayable);
    setAmount(
      parseFloat(
        (selectedService.settings?.websitePrice || "0").replace(/[^\d.-]/g, "")
      ).toFixed(2) * 1
    );
  }

  getASlots();
}, [
  selectedPractitioner,
  selectedService,
  selectedPractice,
  selectedLocation,
  selectedDate,
  onlineBooking,
  ozowStatus,
]);

  async function handleAddAppointment() {
    setCreatingSession(true);
    try {
      const result = await createOnlineBookingSession({
        practiceNumber: selectedPractice.practiceNumber,
        bookingContext: {
          serviceId:              selectedService._id,
          serviceName:            selectedService.servicesName,
          practitionerId:         selectedPractitioner.id,
          practitionerName:       `${selectedPractitioner.firstName} ${selectedPractitioner.lastName}`,
          practiceId:             selectedPractice.practiceNumber,
          practiceName:           selectedPractice.practiceName,
          locationName:           selectedLocation?.name || "",
          locationAddress:        selectedLocation?.address1 || "",
          start:                  times.start,
          end:                    times.end,
          transactionRef,
          isUpfrontPayable,
          isOnlinePaymentEnabled,
          amount,
        },
      });
      setIntakeSession({ tokenId: result.tokenId, rawToken: result.rawToken });
    } catch {
      alert("Could not start booking. Please try again.");
    } finally {
      setCreatingSession(false);
    }
  }

  function handleIntakeCompleted(bookingResult) {
    setIntakeSession(null);
    setHoldSecondsLeft(null);
    setBookingConfirmed(bookingResult ?? { confirmed: true });
  }

  // Start / stop the 15-minute countdown when a hold is active
  useEffect(() => {
    if (!intakeSession) { setHoldSecondsLeft(null); return; }
    setHoldSecondsLeft(15 * 60);
    const tick = setInterval(() => {
      setHoldSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(tick);
  }, [intakeSession?.tokenId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-dismiss when hold expires
  useEffect(() => {
    if (holdSecondsLeft !== 0 || !intakeSession) return;
    cancelOnlineBookingSession(intakeSession.tokenId, intakeSession.rawToken);
    setIntakeSession(null);
    setHoldSecondsLeft(null);
    setHoldExpired(true);
    setSelectedTime(null);
  }, [holdSecondsLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleModalClose() {
    if (intakeSession) {
      await cancelOnlineBookingSession(intakeSession.tokenId, intakeSession.rawToken);
      setIntakeSession(null);
      setHoldSecondsLeft(null);
      setSelectedTime(null);
    }
  }

  useEffect(() => {
    if (selectedTime && availableSlots.length > 0) {
      const selectedSlot = availableSlots.find(
        (slot) => slot.start === selectedTime
      );

      if (selectedSlot) {
        setTimes((prevState) => ({
          ...prevState,
          start: selectedSlot.start,
          end: selectedSlot.end,
          // ... other fields
        }));
      }
    }
  }, [selectedTime, availableSlots /* other dependencies */]);

  const titles = ["Start", "Service", "Practitioner", "Practice", "Summary"];

  const filteredServices = selectedPractitioner
    ? services.filter((s) => s.servicesUser?.includes(selectedPractitioner.id))
    : services;

  const filteredPractitioners = selectedService
    ? practitioners.filter((p) => selectedService.servicesUser?.includes(p.id))
    : practitioners;

  const filteredPractices = selectedService
    ? practices.filter((p) =>
        selectedService.servicesPractice?.includes(p.practiceNumber)
      )
    : selectedPractitioner
    ? practices.filter((p) =>
        services
          .filter((s) => s.servicesUser?.includes(selectedPractitioner.id))
          .flatMap((s) => s.servicesPractice)
          .includes(p.practiceNumber)
      )
    : practices;

  if (!onlineBooking) return <div className="text-center py-5">Loading...</div>;

  // --- (The rest of the component is the same as above — continue from the JSX render section)

  return (
    <>
      <Container className="py-3">
        {/* Banner */}
        <Row className="mb-4 border-bottom align-items-center">
          <Col xs="auto" className="mb-2">
            {onlineBooking.logo?.[0] && (
              <Image
                src={onlineBooking.logo[0]}
                alt="Logo"
                style={{ maxHeight: 60 }}
                fluid
              />
            )}
          </Col>
          <Col className="d-flex justify-content-center align-items-center">
            <h2 className="mb-0 mt-0 text-primary fw-normal">
              {onlineBooking.bookingHeading}
            </h2>
          </Col>
          <Col xs="auto" style={{ visibility: "hidden" }}>
            <Image
              src={onlineBooking.logo?.[0]}
              alt=""
              style={{ maxHeight: 60 }}
              fluid
            />
          </Col>
        </Row>

        {(
          // ——— Wizard + Summary ———
          <Row className="g-4">
            {/* Wizard Panel */}
            <Col md={8}>
              <div className="shadow-sm bg-white p-3 rounded mb-4">
                {flow && (
                  <Nav
                    className="justify-content-center step-indicator mb-4 flex-nowrap"
                    variant="pills"
                  >
                    {(flow === "service-first"
                      ? [
                          "Start",
                          "Service",
                          "Practitioner",
                          "Practice",
                          "Summary",
                        ]
                      : [
                          "Start",
                          "Practitioner",
                          "Service",
                          "Practice",
                          "Summary",
                        ]
                    ).map((title, idx) => {
                      let stepClass = "step-future";
                      if (idx === step) stepClass = "active-step";
                      else if (idx < step) stepClass = "step-completed";

                      return (
                        <Nav.Item key={idx} className="d-flex align-items-center">
                          <Nav.Link
                            className={`step-link ${stepClass}`}
                            disabled={idx > step}
                            onClick={() => idx <= step && setStep(idx)}
                          >
                            <span className="step-number me-1">{idx + 1}</span>
                            <span className="step-label">{title}</span>
                          </Nav.Link>
                          {idx < 4 && <span className="step-connector mx-1">›</span>}
                        </Nav.Item>
                      );
                    })}
                  </Nav>
                )}

                {/* Steps */}
                {step === 0 && (
                  <div className="text-center py-2">
                    <h4>How would you like to start?</h4>
                    <p>
                      Welcome to our online booking portal! With the steps below
                      you will be able to create an appointment with us. For
                      your convenience, you can start the journey by choosing
                      the service or your practitioner first. We’ll then show
                      you the earliest available slots.
                    </p>
                    <div className="d-flex flex-column flex-sm-row justify-content-center gap-2">
                      <Button
                        className="px-4 py-2"
                        onClick={() => (setFlow("service-first"), setStep(1))}
                      >
                        Choose a Service
                      </Button>
                      <Button
                        className="px-4 py-2"
                        onClick={() => (setFlow("prac-first"), setStep(1))}
                      >
                        Choose a Practitioner
                      </Button>
                    </div>
                  </div>
                )}

                {step === 1 && flow === "service-first" && (
                  <>
                    <h5>Select a Service</h5>
                    <Row className="g-3">
                      {filteredServices.map((s) => (
                        <Col md={6} key={s._id}>
                          <Card
                            onClick={() => setSelectedService(s)}
                            className={`cursor-pointer ${
                              selectedService?._id === s._id
                                ? "border-primary shadow"
                                : ""
                            }`}
                          >
                            <Card.Body>
                              <h6>{s.servicesName}</h6>
                              <Row>
                                <Col xs={7}>
                                  <small>{s.servicesDescription}</small>
                                </Col>
                                <Col xs={5} className="text-end">
                                  <strong>
                                    {s.settings?.websitePrice
                                      ? `${s.settings.websitePrice}`
                                      : "N/A"}
                                  </strong>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                          
                        </Col>
                      ))}
                    </Row>
                    <Form.Label className="text-primary mt-3">{onlineBooking.serviceNotes}</Form.Label>
                    <div className="mt-4 d-flex justify-content-between">
                      <Button onClick={() => (setFlow(null), setStep(0))}>
                        Back
                      </Button>
                      <Button
                        disabled={!selectedService}
                        onClick={() => setStep(2)}
                      >
                        Next
                      </Button>
                    </div>
                  </>
                )}

                {step === 1 && flow === "prac-first" && (
                  <>
                    <h5>Select a Practitioner</h5>
                    <Row className="g-3">
                      {practitioners.map((p) => (
                        <Col md={4} key={p.id}>
                          <Card
                            onClick={() => setSelectedPractitioner(p)}
                            className={`cursor-pointer ${
                              selectedPractitioner?.id === p.id
                                ? "border-primary shadow"
                                : ""
                            }`}
                          >
                            <Card.Body className="text-center">
                              <Image
                                src={
                                  p.ppicURL || "https://via.placeholder.com/100"
                                }
                                roundedCircle
                                width={100}
                                height={100}
                              />
                              <h6>
                                {p.firstName} {p.lastName}
                              </h6>
                              {p.hpcsa && <small>HPCSA: {p.hpcsa}</small>}
                              <div>
                                <small>{p.occupation}</small>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                    
                    <div className="mt-4 d-flex justify-content-between">
                      <Button onClick={() => setStep(0)}>Back</Button>
                      <Button
                        disabled={!selectedPractitioner}
                        onClick={() => setStep(2)}
                      >
                        Next
                      </Button>
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <h5>
                      {flow === "service-first"
                        ? "Select a Practitioner"
                        : "Select a Service"}
                    </h5>
                    <Row className="g-3">
                      {flow === "service-first"
                        ? filteredPractitioners.map((p) => (
                            <Col md={4} key={p.id}>
                              <Card
                                onClick={() => setSelectedPractitioner(p)}
                                className={`cursor-pointer ${
                                  selectedPractitioner?.id === p.id
                                    ? "border-primary shadow"
                                    : ""
                                }`}
                              >
                                <Card.Body className="text-center">
                                  <Image
                                    src={
                                      p.ppicURL ||
                                      "https://via.placeholder.com/100"
                                    }
                                    roundedCircle
                                    width={100}
                                    height={100}
                                  />
                                  <h6>
                                    {p.firstName} {p.lastName}
                                  </h6>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))
                        : filteredServices.map((s) => (
                            <Col md={6} key={s._id}>
                              <Card
                                onClick={() => setSelectedService(s)}
                                className={`cursor-pointer ${
                                  selectedService?._id === s._id
                                    ? "border-primary shadow"
                                    : ""
                                }`}
                              >
                                <Card.Body>
                                  <h6>{s.servicesName}</h6>
                                  <Row>
                                    <Col xs={7}>
                                      <small>{s.servicesDescription}</small>
                                    </Col>
                                    <Col xs={5} className="text-end">
                                      <strong>
                                        {s.settings?.websitePrice
                                          ? `${s.settings.websitePrice}`
                                          : "N/A"}
                                      </strong>
                                    </Col>
                                  </Row>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                    </Row>
                    <Form.Label className="text-primary mt-3">{onlineBooking.serviceNotes}</Form.Label>
                    <div className="mt-4 d-flex justify-content-between">
                      <Button onClick={() => setStep(1)}>Back</Button>
                      <Button
                        disabled={
                          flow === "service-first"
                            ? !selectedPractitioner
                            : !selectedService
                        }
                        onClick={() => setStep(3)}
                      >
                        Next
                      </Button>
                    </div>
                  </>
                )}

                {step === 3 && (
                  <>
                    <h5>Select a Practice</h5>
                    <Row className="g-3">
                      {filteredPractices.map((p) => (
                        <Col md={4} key={p._id}>
                          <Card
                            onClick={() => {
                              setSelectedPractice(p);
                              // Reset location when switching practice
                              const locs = p.locations || [];
                              if (locs.length === 1) {
                                setSelectedLocation(locs[0]);
                              } else {
                                setSelectedLocation(null);
                              }
                            }}
                            className={`cursor-pointer ${
                              selectedPractice?._id === p._id
                                ? "border-primary shadow"
                                : ""
                            }`}
                          >
                            <Card.Body>
                              <h6>{p.practiceName}</h6>
                              <small>
                                {p.practiceCity}, {p.practicePostalCode}
                              </small>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>

                    {/* Location selection for the chosen practice */}
                    {selectedPractice && (
                      <>
                        <h6 className="mt-4">Select a Location</h6>
                        <Row className="g-3">
                          {(selectedPractice.locations &&
                          selectedPractice.locations.length
                            ? selectedPractice.locations
                            : [
                                {
                                  name:
                                    selectedPractice.primaryLocationName ||
                                    "Main Location",
                                  address1: selectedPractice.practiceAddress,
                                  address2: selectedPractice.practiceAddress2,
                                  city: selectedPractice.practiceCity,
                                  province: selectedPractice.practiceProvince,
                                  postalCode:
                                    selectedPractice.practicePostalCode,
                                },
                              ]
                          ).map((loc, idx) => {
                            const isSelected =
                              selectedLocation &&
                              selectedLocation.name === loc.name &&
                              selectedLocation.city === loc.city;

                            return (
                              <Col md={6} key={`${loc.name}-${idx}`}>
                                <Card
                                  onClick={() => setSelectedLocation(loc)}
                                  className={`cursor-pointer ${
                                    isSelected ? "border-primary shadow" : ""
                                  }`}
                                >
                                  <Card.Body className="py-2">
                                    <div className="fw-semibold">
                                      {loc.name}
                                    </div>
                                    <div className="small text-muted">
                                      {loc.address1}
                                      {loc.address2 ? `, ${loc.address2}` : ""}
                                    </div>
                                    <div className="small text-muted">
                                      {loc.city} {loc.postalCode}
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                        <Form.Label className="text-primary mt-3">{onlineBooking.practiceNotes}</Form.Label>
                      </>
                    )}

                    <div className="mt-4 d-flex justify-content-between">
                      <Button onClick={() => setStep(2)}>Back</Button>
                      <Button
                        disabled={!selectedPractice || !selectedLocation}
                        onClick={() => setStep(4)}
                      >
                        Next
                      </Button>
                    </div>
                  </>
                )}

                {bookingConfirmed && (
                  <div className="text-center py-5">
                    <div style={{ fontSize: 48, color: "green" }}>&#10003;</div>
                    <h4 className="mt-3">Reservation made</h4>
                    <p className="text-muted">
                      Your timeslot has been reserved. It's only confirmed once you receive an email or sms from the Practice.
                    </p>
                  </div>
                )}

                {step === 4 && !bookingConfirmed && (
                  <>
                    <h5>Select Date & Time</h5>
                    {holdExpired && (
                      <div className="alert alert-warning py-2 small mb-3">
                        Your slot reservation expired. Please select a new time.
                      </div>
                    )}
                    <ListGroup variant="flush">
                      <ListGroup.Item>
                        <div className="d-flex align-items-center">
                          <Image
                            src={
                              selectedPractitioner?.ppicURL ||
                              "https://via.placeholder.com/60"
                            }
                            roundedCircle
                            width={60}
                            height={60}
                            className="me-3"
                          />
                          <div>
                            <div>
                              <strong>
                                {selectedPractitioner?.firstName}{" "}
                                {selectedPractitioner?.lastName}
                              </strong>
                            </div>
                            <div className="text-muted">
                              {selectedService?.servicesName}
                            </div>
                          </div>
                        </div>
                      </ListGroup.Item>
                    </ListGroup>
                    <h6 className="text-primary mt-3">Available Slots:</h6>
                    {availableSlots.length > 0 ? (
                      <Nav
                        variant="pills"
                        className="flex-wrap mb-3 step-indicator"
                        onSelect={(time) => setSelectedTime(time)}
                        activeKey={selectedTime}
                      >
                        {availableSlots.map((slot) => (
                          <Nav.Item key={slot.start} className="me-2 mb-2">
                            <Nav.Link
                              eventKey={slot.start}
                              className="step-link"
                            >
                              {slot.start.slice(11)} - {slot.end.slice(11)}
                            </Nav.Link>
                          </Nav.Item>
                        ))}
                      </Nav>
                    ) : (
                      <p className="text-muted">
                        No times available for this date.
                      </p>
                    )}
                    <Card className="mt-3 p-3">
                      <MonthCalendar
                        selectedDate={selectedDate}
                        onSelectDate={(date) => setSelectedDate(date)}
                      />
                    </Card>
                    <div className="mt-4 d-flex justify-content-between">
                      <Button onClick={() => setStep(3)} disabled={creatingSession}>Back</Button>
                      <Button
                        variant="success"
                        disabled={!selectedTime || creatingSession}
                        onClick={handleAddAppointment}
                      >
                        {creatingSession
                          ? <><Spinner as="span" size="sm" animation="border" className="me-2" />Please wait…</>
                          : "Confirm"}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Col>

            {/* Live Booking Summary */}
            <Col md={4}>
              <Card>
                <Card.Header>
                  <h5 className="text-primary">Booking Summary</h5>
                </Card.Header>
                <Card.Body>
                  <Row className="mb-2">
                    <Col xs={4}>
                      <strong>Practitioner:</strong>
                    </Col>
                    <Col xs={8}>
                      {selectedPractitioner
                        ? `${selectedPractitioner.firstName} ${selectedPractitioner.lastName}`
                        : ""}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={4}>
                      <strong>Practice:</strong>
                    </Col>
                    <Col xs={8}>{selectedPractice?.practiceName || ""}</Col>

                    <Col xs={4}></Col>
                    <Col xs={8}>
                      {selectedLocation
                        ? `${selectedLocation.address1 || ""} ${
                            selectedLocation.address2 || ""
                          }`
                        : `${selectedPractice?.practiceAddress || ""} ${
                            selectedPractice?.practiceAddress2 || ""
                          }`}
                    </Col>

                    <Col xs={4}></Col>
                    <Col xs={8}>
                      {selectedLocation
                        ? `${selectedLocation.city || ""} ${
                            selectedLocation.postalCode || ""
                          }`
                        : `${selectedPractice?.practiceCity || ""} ${
                            selectedPractice?.practicePostalCode || ""
                          }`}
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Col xs={4}>
                      <strong>Service:</strong>
                    </Col>
                    <Col xs={8}>{selectedService?.servicesName || ""}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={4}>
                      <strong>Price:</strong>
                    </Col>
                    <Col xs={8}>
                      {selectedService?.settings.websitePrice || ""}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={4}>
                      <strong>Date & Time:</strong>
                    </Col>
                    <Col xs={8}>
                      {selectedTime
                        ? new Date(selectedTime).toLocaleString(undefined, {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })
                        : ""}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <div className="mt-3">
                <BookingSummary
                  selectedPractice={selectedPractice}
                  selectedLocation={selectedLocation}
                  practices={practices}
                />
              </div>
            </Col>
          </Row>
        )}
      </Container>

      {/* Footer */}
      <Row className="w-100 justify-content-start px-2 mt-5 border-top">
        <Col md="auto" className="text-start">
          <small className="text-muted">Powered by:</small>
          <Image
            src="https://practostorage.blob.core.windows.net/practopublic/logo.png"
            alt="PractoSync"
            style={{ maxHeight: "50px", marginLeft: "10px" }}
            fluid
          />
        </Col>
      </Row>

      <Modal
        show={!!intakeSession}
        dialogClassName="modal-50w"
        backdrop="static"
        keyboard={false}
        onHide={handleModalClose}
      >
        <Modal.Header className="border-bottom" closeButton>
          <Modal.Title className="text-primary fw-normal d-flex align-items-center gap-3">
            Complete Your Booking
            {holdSecondsLeft !== null && holdSecondsLeft < 120 && (
              <span className="small fw-normal text-danger" style={{ fontSize: "0.85rem" }}>
                {Math.floor(holdSecondsLeft / 60)}:{String(holdSecondsLeft % 60).padStart(2, "0")} remaining
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ overflowY: "auto" }}>
          {intakeSession && (
            <IntakeForm
              tokenIdProp={intakeSession.tokenId}
              rawTokenProp={intakeSession.rawToken}
              embedded
              onCompleted={handleIntakeCompleted}
            />
          )}
        </Modal.Body>
      </Modal>

    </>
  );
}
